import { useMutation, useQuery, useQueryClient } from "react-query";
import { useForm } from "react-hook-form";
import { useAuth } from "features/auth";
import { api, ApiError } from "utils/api";
import type { ReviewVote } from "../types";

type ReviewVoteType = { reviewableId: string; reviewableType: string };

/**
 * Fetch a user's review votes for a given Project or Person
 */
const getReviewVotes = (type: ReviewVoteType) =>
  type.reviewableType === "PROJECT"
    ? api(`/profile/reactions/reviews?projectId=${type.reviewableId}`)
    : api(`/profile/reactions/reviews?artistId=${type.reviewableId}`);

type ReviewVoteResp = Omit<ReviewVote, "vote"> & {
  reaction: ReviewVote["vote"];
};

/**
 * Custom hook to fetch a user's review votes for a given Project or Person
 */
const useReviewVotes = (type: ReviewVoteType) => {
  const { user } = useAuth();

  const baseKey = [type.reviewableType, type.reviewableId];

  return useQuery<ReviewVoteResp[], ApiError, ReviewVote[]>(
    [...baseKey, "reviews", "votes"],
    () => getReviewVotes(type),
    {
      enabled: !!(user && type),
      select: (resp) =>
        resp.map(({ reaction, ...rest }) => ({
          // NOTE: transform backend ReviewVote shape for consistent naming
          vote: reaction,
          ...rest,
        })),
    }
  );
};

type BaseReviewVoteUpdates = ReviewVoteType & {
  reviewId: string;
};

type ReviewVoteFormFields = {
  moderatorFeedback?: string;
  vote: ReviewVote["vote"];
};

type ReviewVoteUpdates = BaseReviewVoteUpdates & ReviewVoteFormFields;

// type UpdateReviewVoteResp = {
//   reaction: ReviewVote;
//   review: ProjectReview | ArtistReview;
// };

const updateReviewVote = async ({ vote, ...updates }: ReviewVoteUpdates) =>
  api("/reviews/react", {
    method: "PUT",
    body: JSON.stringify({ ...updates, reaction: vote }),
  });

const useUpdateReviewVote = (updates: BaseReviewVoteUpdates) => {
  const queryClient = useQueryClient();

  const { mutate, ...mutation } = useMutation(
    (fields: ReviewVoteFormFields) =>
      updateReviewVote({ ...updates, ...fields }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          updates.reviewableType,
          updates.reviewableId,
          "reviews",
        ]);

        queryClient.invalidateQueries(["reviews", "pending"]);
      },
    }
  );

  return { updateReviewVote: mutate, ...mutation };
};

const useModeratorFeedbackForm = () =>
  useForm<ReviewVoteFormFields>({
    defaultValues: {
      moderatorFeedback: "",
      vote: "downvote",
    },
  });

export { useReviewVotes, useUpdateReviewVote, useModeratorFeedbackForm };
